import { createFileRoute, Outlet, useRouterState } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { getCameraProvider, useNativeCameraSlotActive } from "@/lib/camera";
import { useStore } from "@/store";

export const Route = createFileRoute("/$locale/$partner/_app/_card")({
	component: CardLayout,
});

function CardLayout() {
	const themeMode = useStore((state) => state.themeMode);
	// The native camera preview shows through a hole in a fixed backdrop, so the card must not
	// paint its own background over it while a slot is mounted.
	const nativeCameraSlotActive = useNativeCameraSlotActive();
	// A fading background would show the wrapper's preview layer through the page while it runs.
	const backgroundTransition = getCameraProvider() === "native" ? "" : "transition-all duration-500 ease-in-out";
	// While a route transition is in flight the outgoing page stays mounted and
	// clickable, so a rapid second/spam tap would land on a stale button (or the
	// next page's) and advance an extra step. Suppressing pointer events on the
	// card content for the pending phase drops those taps centrally — no per-page
	// guard, and it never fires for programmatic navigation.
	const isNavigating = useRouterState({ select: (s) => s.status === "pending" });
	return (
		<div
			className={`w-full h-screen-minus-58 sm:h-screen-minus-64 px-4 pb-4 md:px-10 md:pb-10 ${
				nativeCameraSlotActive ? "bg-transparent" : themeMode === "dark" ? "bg-zinc-900" : "bg-white shadow-xl"
			} sm:rounded-xl overflow-hidden flex flex-col ${backgroundTransition} relative`}
		>
			<AnimatePresence mode="wait">
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.5 }}
					className="flex-1 h-full"
					style={{ pointerEvents: isNavigating ? "none" : undefined }}
				>
					<Outlet />
				</motion.div>
			</AnimatePresence>
		</div>
	);
}
